import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, FileText, PlusCircle, LogOut, Users, Building2, Menu, X,
  BarChart3, Briefcase, Activity, Receipt, Gift, Calendar, Map, Home, Navigation, Settings,
} from 'lucide-react';
import { isPreviewEnv } from './PreviewPortalRedirect';
import './portal-v2.css';

const PortalV2Layout = ({ children, eyebrow, title, subtitle, action, crumbs }) => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { document.title = 'WesTex Legal Portal'; }, []);

  const handleLogout = () => { logout(); navigate('/portal/login'); };

  // Hide the V2 preview banner when V2 IS the default (preview env). Show it
  // only on production where V2 is a parallel preview that admins are testing.
  const showPreviewBanner = !isPreviewEnv();

  const userNav = [
    { name: 'Dashboard',       href: '/portal/dashboard', icon: LayoutDashboard },
    { name: 'My Jobs',         href: '/portal/jobs',      icon: FileText },
    { name: 'Activity',        href: '/portal/activity',  icon: Activity },
    { name: 'Submit New Job',  href: '/portal/submit',    icon: PlusCircle },
    { name: 'Invoices',        href: '/portal/invoices',  icon: Receipt },
    { name: 'Settings',        href: '/portal/settings',  icon: Settings },
  ];
  const adminNav = [
    { name: 'Admin Dashboard', href: '/portal/admin',           icon: BarChart3 },
    { name: 'All Jobs',        href: '/portal/admin/jobs',      icon: Briefcase },
    { name: 'Jobs Map',        href: '/portal/admin/jobs-map',  icon: Map },
    { name: 'Find Server',     href: '/portal/admin/server-finder', icon: Navigation },
    { name: 'Invoices',        href: '/portal/admin/invoices',  icon: Receipt },
    { name: 'Notary Bookings', href: '/portal/admin/notary',    icon: Calendar },
    { name: 'Referrals',       href: '/portal/admin/referrals', icon: Gift },
    { name: 'Manage Clients',  href: '/portal/admin/clients',   icon: Users },
    { name: 'Approved Domains',href: '/portal/admin/domains',   icon: Building2 },
  ];

  // Exact-match for "root" admin/client dashboards so they don't stay highlighted
  // when navigating into deeper /admin/* or sub-routes. Sub-pages get prefix-match.
  const ROOT_PATHS = ['/portal/admin', '/portal/dashboard'];
  const isActive = (href) => {
    if (ROOT_PATHS.includes(href)) return location.pathname === href;
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const NavLink = ({ item }) => {
    const active = isActive(item.href);
    return (
      <Link
        to={item.href}
        onClick={() => setOpen(false)}
        className={`pv2-nav-link ${active ? 'pv2-nav-link-active' : ''}`}
        data-testid={`pv2-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <item.icon size={16} />
        <span>{item.name}</span>
      </Link>
    );
  };

  return (
    <div className="portal-v2">
      {/* Subtle Lubbock skyline background — extremely opaque for atmospheric
          depth without competing with content. Shown sitewide across portal. */}
      <div
        className="pv2-admin-bg"
        style={{ backgroundImage: 'url(/lubbock-skyline.jpg)' }}
        aria-hidden="true"
        data-testid="pv2-bg"
      />
      {/* Preview banner — only visible on production where V2 is a parallel preview.
          In the preview environment, V2 is already the default, so no banner is needed. */}
      {showPreviewBanner && (
        <div className="pv2-preview-banner" data-testid="pv2-preview-banner">
          Portal V2 Preview
          <Link to={isAdmin ? '/portal/admin' : '/portal/dashboard'} data-testid="pv2-back-to-v1">← Back to V1 Portal</Link>
        </div>
      )}

      {/* Mobile backdrop */}
      {open && <div className="pv2-mobile-backdrop" onClick={() => setOpen(false)} />}

      {/* Sidebar */}
      <aside className={`pv2-sidebar ${open ? 'pv2-sidebar-open' : ''}`}>
        <div className="pv2-sidebar-brand">
          <div className="pv2-sidebar-brand-logo">
            <LayoutDashboard size={18} color="#fff" strokeWidth={1.6} />
          </div>
          <div>
            <div className="pv2-sidebar-brand-text" style={{ whiteSpace: 'nowrap' }}>WesTex Portal</div>
          </div>
        </div>

        <nav style={{ flex: 1, overflowY: 'auto', paddingBottom: 12 }}>
          <Link to="/" className="pv2-nav-link" style={{ marginTop: 12 }}>
            <Home size={16} /> <span>Public Site</span>
          </Link>

          {!isAdmin && (
            <div className="pv2-nav-section">
              <div className="pv2-nav-section-label">Portal</div>
              {userNav.map(item => <NavLink key={item.href} item={item} />)}
            </div>
          )}
          {isAdmin && (
            <div className="pv2-nav-section">
              <div className="pv2-nav-section-label">Admin</div>
              {adminNav.map(item => <NavLink key={item.href} item={item} />)}
            </div>
          )}
        </nav>

        <div className="pv2-sidebar-foot">
          <div className="pv2-sidebar-user-name" data-testid="pv2-user-name">
            {user?.first_name} {user?.last_name}
          </div>
          <div className="pv2-sidebar-user-meta">{user?.email}</div>
          {user?.company_name && <div className="pv2-sidebar-user-meta">{user.company_name}</div>}
          <button onClick={handleLogout} className="pv2-sidebar-logout" data-testid="pv2-logout">
            <LogOut size={14} strokeWidth={1.5} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="pv2-shell">
        <header className="pv2-topbar">
          <button
            className="pv2-mobile-toggle"
            onClick={() => setOpen(true)}
            data-testid="pv2-open-sidebar"
            aria-label="Open menu"
          >
            <Menu size={18} />
          </button>
          <div className="pv2-topbar-crumbs">
            {crumbs ? crumbs : <strong>{title || 'Dashboard'}</strong>}
          </div>
          <div className="pv2-topbar-right">
            {isAdmin ? (
              <span className="pv2-admin-tag" data-testid="pv2-admin-tag">Admin</span>
            ) : (
              <span className="pv2-user-tag" data-testid="pv2-user-tag">
                {[user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'Account'}
              </span>
            )}
          </div>
        </header>

        <main className="pv2-page">
          {(title || eyebrow) && (
            <div className="pv2-page-head">
              <div>
                {eyebrow && <div className="pv2-page-eyebrow">{eyebrow}</div>}
                {title && <h1 className="pv2-page-title">{title}</h1>}
                {subtitle && <p className="pv2-page-subtitle">{subtitle}</p>}
              </div>
              {action && <div>{action}</div>}
            </div>
          )}
          {children}

          {/* Need Assistance footer — client portal only */}
          {!isAdmin && (
            <div className="pv2-help-banner" data-testid="pv2-need-assistance">
              <div>
                <div className="pv2-help-title">Need Assistance?</div>
                <div className="pv2-help-body">
                  Call <a href="tel:8064011749">(806) 401-1749</a> or email <a href="mailto:WesTexLegal@gmail.com">WesTexLegal@gmail.com</a>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default PortalV2Layout;
